import { CalendarDays } from 'lucide-react';
import { DateTime, Info } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip } from '@/components/ui/tooltip';
import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { formatDate, formatTime } from '@/utils/format';

import {
  DASHBOARD_WIDGET_ROUTES,
  type DashboardCalendarDay,
  type DashboardCalendarEvent,
} from './dashboard-content';

const TOOLTIP_EVENT_LIMIT = 6;

const firstDayOfWeek = (locale: string): number => {
  try {
    const loc = new Intl.Locale(locale);
    const value = (loc as Intl.Locale & { weekInfo?: { firstDay: number } }).weekInfo?.firstDay;
    if (value === 7) return 7;
    if (value && value >= 1 && value <= 7) return value;
  } catch {
    // fall through
  }
  return 1;
};

const buildGrid = (month: DateTime, weekStart: number): Array<string | null> => {
  const start = month.startOf('month');
  const end = month.endOf('month');
  const pad = (start.weekday - weekStart + 7) % 7;
  const cells: Array<string | null> = Array.from({ length: pad }, () => null);
  let cursor = start;
  while (cursor <= end) {
    cells.push(cursor.toISODate());
    cursor = cursor.plus({ days: 1 });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

export const DashboardCalendarTile = ({
  month,
  days,
  loading,
  compact = false,
  className,
}: {
  month: string;
  days: DashboardCalendarDay[];
  loading: boolean;
  compact?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation('reports');
  const { t: tCommon } = useTranslation();
  const timezone = usePreferences((state) => state.timezone);
  const locale = usePreferences((state) => state.locale);
  const today = DateTime.now().setZone(timezone).toISODate();
  const cursor = DateTime.fromISO(`${month}-01`, { zone: timezone }).isValid
    ? DateTime.fromISO(`${month}-01`, { zone: timezone })
    : DateTime.now().setZone(timezone).startOf('month');

  const weekStart = useMemo(() => firstDayOfWeek(locale), [locale]);
  const weekdayNames = useMemo(() => {
    const names = Info.weekdays('narrow', { locale });
    return [...names.slice(weekStart - 1), ...names.slice(0, weekStart - 1)];
  }, [locale, weekStart]);
  const cells = useMemo(() => buildGrid(cursor, weekStart), [cursor, weekStart]);
  const dayByDate = useMemo(() => new Map(days.map((day) => [day.date, day])), [days]);
  const monthLabel = cursor.setLocale(locale).toFormat('LLLL yyyy');
  const hasEvents = days.length > 0;

  return (
    <Card
      className={cn(
        'flex h-full flex-col overflow-hidden [&>div:last-child]:min-h-0 [&>div:last-child]:flex-1',
        className,
      )}
    >
      <CardHeader
        className={cn(
          'flex-row items-center justify-between gap-3',
          compact ? 'p-3 pb-2' : undefined,
        )}
      >
        <CardTitle className="flex items-center gap-2 text-card-title">
          <CalendarDays className="size-4 text-fg-tertiary" aria-hidden />
          {t('dashboard.calendar.title')}
        </CardTitle>
        {compact ? null : (
          <Button variant="ghost" size="sm" asChild>
            <Link to={DASHBOARD_WIDGET_ROUTES.calendar!}>{tCommon('action.viewAll')}</Link>
          </Button>
        )}
      </CardHeader>
      <CardContent
        className={cn(
          'flex-1 overflow-hidden',
          compact ? 'p-3 pt-0' : 'pt-0',
        )}
      >
        <p className="mb-2 text-body-sm font-medium text-fg-secondary">{monthLabel}</p>
        {loading ? (
          <p className="text-body-sm text-fg-muted">…</p>
        ) : !hasEvents ? (
          <p className="text-body-sm text-fg-muted">{t('dashboard.calendar.empty')}</p>
        ) : null}
        <div className="grid grid-cols-7 gap-1">
          {weekdayNames.map((name) => (
            <div
              key={name}
              className="px-0.5 text-center text-caption font-medium text-fg-secondary"
            >
              {name}
            </div>
          ))}
          {cells.map((date, index) => {
            if (!date) {
              return <div key={`pad-${index}`} className="aspect-square rounded-sm" />;
            }
            const day = dayByDate.get(date);
            const events = day?.events ?? [];
            const showDot = events.length > 0 || (day?.count ?? 0) > 0;
            const isToday = date === today;
            const dateLabel = formatDate(date, { locale, style: 'medium' });
            const cell = (
              <Link
                to={DASHBOARD_WIDGET_ROUTES.calendar!}
                className={cn(
                  'flex aspect-square flex-col items-center justify-center rounded-sm text-caption tabular-nums',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                  isToday ? 'bg-brand text-on-brand' : 'text-fg hover:bg-surface-sunken',
                )}
                aria-label={
                  events.length > 0
                    ? t('dashboard.calendar.dayAriaWithEvents', {
                        date: dateLabel,
                        events: events.map((event) => event.title).join(', '),
                      })
                    : t('dashboard.calendar.dayAria', { date: dateLabel })
                }
              >
                <span>{DateTime.fromISO(date).day}</span>
                {showDot ? (
                  <span
                    className={cn(
                      'mt-0.5 size-1 rounded-full',
                      isToday ? 'bg-on-brand' : 'bg-brand',
                    )}
                    aria-hidden
                  />
                ) : (
                  <span className="mt-0.5 size-1" aria-hidden />
                )}
              </Link>
            );

            if (events.length === 0) {
              return <div key={date}>{cell}</div>;
            }

            return (
              <Tooltip
                key={date}
                side="top"
                delayDuration={150}
                content={<CalendarDayTooltip events={events} locale={locale} />}
              >
                {cell}
              </Tooltip>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const CalendarDayTooltip = ({
  events,
  locale,
}: {
  events: DashboardCalendarEvent[];
  locale: string;
}) => {
  const { t } = useTranslation('reports');
  const visible = events.slice(0, TOOLTIP_EVENT_LIMIT);
  const overflow = events.length - visible.length;

  return (
    <div className="space-y-1">
      <ul className="space-y-0.5">
        {visible.map((event) => (
          <li key={event.id}>
            {event.title}
            {event.startsAt ? ` · ${formatTime(event.startsAt, { locale })}` : null}
          </li>
        ))}
      </ul>
      {overflow > 0 ? (
        <p className="text-fg-inverse/80">{t('dashboard.calendar.moreEvents', { count: overflow })}</p>
      ) : null}
    </div>
  );
};
